import React, { memo, useCallback, useMemo } from 'react';
import { FlatList, StyleSheet, Text, TouchableOpacity, View } from 'react-native';
import Icon from 'react-native-vector-icons/Feather';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { useNavigation } from '@react-navigation/native';
import type { NativeStackNavigationProp } from '@react-navigation/native-stack';
import EventCard from '../components/EventCard';
import LoadingSpinner from '../components/LoadingSpinner';
import EmptyState from '../components/EmptyState';
import TonyteBackground from '../components/TonyteBackground';
import { useFeed, useActivityInterested } from '../hooks/useFeed';
import { useProfile } from '../hooks/useProfile';
import { useTopPicks } from '../hooks/useTopPicks';
import { useCityContext } from '../context/CityContext';
import { useLocationCoords } from '../hooks/useLocation';
import { auth } from '../firebase';
import { Colors, Spacing, Typography } from '../theme';
import type { RankedEvent } from '../utils/eventRecommendations';
import type { RootStackParamList, TonyteActivity } from '../types';
import { isEventActivity } from '../utils/activityKind';
import { getResolvedEventId } from '../utils/eventIds';
import { ANALYTICS_SOURCES, trackEventDetailOpen } from '../utils/eventAnalytics';
import { useI18n } from '../i18n';
import { useTheme } from '../theme/useTheme';
import { daylightStyle } from '../theme/daylightStyle';
import type { TonyteTheme } from '../theme/themes';

// The full extent of "Top Picks for You" — the module on the Events screen
// only ever shows a short, diversified preview of this same ranked list
// (see rankTopPicks' `limit`); "See all" simply asks for more of it.
const SEE_ALL_LIMIT = 30;

const AllTopPicksScreen: React.FC = () => {
  const { t } = useI18n();
  const { theme } = useTheme();
  const appearance = useMemo(() => createThemeStyles(theme), [theme]);
  const insets = useSafeAreaInsets();
  const navigation = useNavigation<NativeStackNavigationProp<RootStackParamList>>();
  const userId = auth.currentUser?.uid || null;
  const { profile, loading: profileLoading } = useProfile(userId);
  const { activities, loading: feedLoading } = useFeed();
  const { selectedCityKey } = useCityContext();
  const userCoords = useLocationCoords();

  const candidates = useMemo(() => activities.filter(isEventActivity), [activities]);
  const topPicks = useTopPicks({
    userId,
    profile,
    profileReady: !profileLoading,
    candidates,
    excludeEventIds: [],
    cityKey: selectedCityKey,
    userCoords,
    limit: SEE_ALL_LIMIT,
  });

  const handleCardPress = useCallback((activity: TonyteActivity) => {
    trackEventDetailOpen({ eventId: getResolvedEventId(activity), organizerId: activity.hostId, source: ANALYTICS_SOURCES.TOP_PICKS_FOR_YOU });
    navigation.navigate('EventDetail', { eventId: getResolvedEventId(activity), event: activity });
  }, [navigation]);

  const renderItem = useCallback(({ item }: { item: RankedEvent }) => (
    <AllTopPicksCard activity={item.activity} userId={userId} onPress={handleCardPress} />
  ), [handleCardPress, userId]);

  const title = topPicks.mode === 'personalized' ? t('feed.topPicksForYou') : t('feed.popularNearYou');
  const isLoading = feedLoading || topPicks.status === 'loading';

  return (
    <TonyteBackground appearanceAware style={[styles.container, appearance.container]}>
      <View style={[styles.header, { paddingTop: insets.top + 12 }]}>
        <TouchableOpacity style={[styles.backButton, appearance.control]} onPress={() => navigation.goBack()} activeOpacity={0.78}>
          <Icon name="arrow-left" size={20} color={theme.colors.icon} />
        </TouchableOpacity>
        <View style={styles.headerCenter}>
          <Text style={[styles.headerKicker, appearance.textMuted]}>{t('feed.topPicksEyebrow', 'Recommended for you')}</Text>
          <Text style={[styles.headerTitle, appearance.textPrimary]} numberOfLines={1}>{title}</Text>
        </View>
      </View>

      {isLoading ? (
        <LoadingSpinner appearanceAware label={t('feed.loadingEvents')} />
      ) : (
        <FlatList
          data={topPicks.items}
          keyExtractor={entry => entry.activity.id}
          renderItem={renderItem}
          contentContainerStyle={[styles.list, { paddingBottom: insets.bottom + 32 }]}
          showsVerticalScrollIndicator={false}
          ListEmptyComponent={
            <EmptyState icon="zap" title={t('feed.noEventsFound')} subtitle={t('feed.noEventsSub')} />
          }
        />
      )}
    </TonyteBackground>
  );
};

const AllTopPicksCard: React.FC<{ activity: TonyteActivity; userId: string | null; onPress: (activity: TonyteActivity) => void }> = memo(
  ({ activity, userId, onPress }) => {
    const { interested, count, toggle } = useActivityInterested(getResolvedEventId(activity), userId, activity);
    return (
      <EventCard
        activity={activity}
        onPress={() => onPress(activity)}
        onInterestPress={() => void toggle()}
        interested={interested}
        interestedCount={count}
      />
    );
  },
);

const styles = StyleSheet.create({
  container: { flex: 1, backgroundColor: 'transparent' },
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: 20,
    paddingBottom: 12,
    gap: 12,
  },
  backButton: {
    width: 40,
    height: 40,
    borderRadius: 14,
    borderWidth: 1,
    borderColor: 'rgba(255,255,255,0.1)',
    backgroundColor: 'rgba(255,255,255,0.07)',
    alignItems: 'center',
    justifyContent: 'center',
  },
  headerCenter: { flex: 1, minWidth: 0 },
  headerKicker: {
    color: 'rgba(255,255,255,0.4)',
    fontSize: 9,
    lineHeight: 12,
    fontWeight: Typography.extrabold,
    letterSpacing: 2,
    textTransform: 'uppercase',
    marginBottom: 2,
  },
  headerTitle: {
    color: Colors.textPrimary,
    fontSize: 20,
    lineHeight: 24,
    fontWeight: Typography.extrabold,
  },
  list: {
    paddingHorizontal: Spacing.base,
    paddingTop: Spacing.sm,
  },
});

const createThemeStyles = (theme: TonyteTheme) => StyleSheet.create({
  container: daylightStyle(theme, { backgroundColor: theme.colors.background }),
  control: daylightStyle(theme, {
    backgroundColor: theme.colors.surfaceElevated,
    borderColor: theme.colors.border,
  }),
  textPrimary: daylightStyle(theme, { color: theme.colors.textPrimary }),
  textMuted: daylightStyle(theme, { color: theme.colors.textMuted }),
});

export default AllTopPicksScreen;
